import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, Rocket } from "lucide-react";
import { WrenchIcon } from "@/components/ui/wrench";
import type {
  DbAnnouncementCategory,
  DbAnnouncementIncidentState,
  DbAnnouncementMaintenanceState,
  DbAnnouncementPost,
} from "@/types/db";

// Pick the announcement to feature. Prefer whichever post is explicitly
// featured; fall back to the most recent unresolved incident if nothing has
// been flagged yet (callers pass posts ordered newest-first) — an ongoing
// outage should never silently drop off the banner just because nobody got
// around to flagging it. Shared by the announcements page and the chat
// header's featured chip so both always agree on what is featured.
export function selectFeaturedAnnouncement<
  T extends Pick<DbAnnouncementPost, "category" | "incident_state" | "is_featured">,
>(posts: T[]): T | null {
  const explicit = posts.find((p) => p.is_featured);
  if (explicit) return explicit;
  return (
    posts.find(
      (p) => p.category === "incident" && p.incident_state && p.incident_state !== "resolved"
    ) ?? null
  );
}

export const CATEGORY_LABEL: Record<DbAnnouncementCategory, string> = {
  incident: "Incident",
  release: "Release",
  maintenance: "Maintenance",
};

export const CATEGORY_ICON: Record<DbAnnouncementCategory, React.ElementType> = {
  incident: AlertTriangle,
  release: Rocket,
  maintenance: WrenchIcon,
};

// Icon-tile color per category, shown on every card regardless of active/
// resolved state — same palette already used below for INCIDENT_STATE_CLASS.
export const CATEGORY_ICON_CLASS: Record<DbAnnouncementCategory, string> = {
  incident: "bg-destructive/15 text-destructive",
  release: "bg-green-500/15 text-green-800 dark:text-green-300",
  maintenance: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
};

// Featured-row accent border, matching CATEGORY_ICON_CLASS's palette — a
// featured release/maintenance post shouldn't get the incident's alarm red.
export const CATEGORY_BORDER_CLASS: Record<DbAnnouncementCategory, string> = {
  incident: "border-destructive/40",
  release: "border-green-500/40",
  maintenance: "border-amber-500/40",
};

// Faint per-category tint for the featured row's background, same palette
// at very low opacity so the row reads as highlighted without competing
// with the icon tile or badges.
export const CATEGORY_FEATURED_BG_CLASS: Record<DbAnnouncementCategory, string> = {
  incident: "bg-destructive/5",
  release: "bg-green-500/5",
  maintenance: "bg-amber-500/5",
};

export const INCIDENT_STATE_LABEL: Record<DbAnnouncementIncidentState, string> = {
  investigating: "Investigating",
  identified: "Identified",
  resolved: "Resolved",
};

export const INCIDENT_STATE_CLASS: Record<DbAnnouncementIncidentState, string> = {
  investigating: "bg-destructive/15 text-destructive",
  identified: "bg-amber-500/15 text-amber-600",
  resolved: "bg-green-500/15 text-green-600",
};

export const INCIDENT_STATE_OPTIONS: DbAnnouncementIncidentState[] = [
  "investigating",
  "identified",
  "resolved",
];

export const MAINTENANCE_STATE_LABEL: Record<DbAnnouncementMaintenanceState, string> = {
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed: "Completed",
};

export const MAINTENANCE_STATE_CLASS: Record<DbAnnouncementMaintenanceState, string> = {
  scheduled: "bg-muted text-muted-foreground",
  in_progress: "bg-amber-500/15 text-amber-600",
  completed: "bg-green-500/15 text-green-600",
};

export const MAINTENANCE_STATE_OPTIONS: DbAnnouncementMaintenanceState[] = [
  "scheduled",
  "in_progress",
  "completed",
];

// Relative timestamp ("5 minutes ago", "3 days ago") — same date-fns
// convention used for list cards in meetings/page.tsx and chats/page.tsx.
export function formatRelativeTime(iso: string): string {
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
}

export function formatFullTimestamp(iso: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(iso)
  );
}
